import { z } from 'zod/mini';
import { CartAndLineItemGroups } from '@/types/commercetools';
import {
  BUSINESS_UNIT,
  CategoryIdsByProductType,
  CouponValidationRequest,
  ValidationItem,
  getCartValidationItems,
} from './couponValidation';

// Strike prices via pricing-service `POST /api/v1/pricing/validation`: one basket per displayed
// item ("candidate"), holding the cart's items plus that candidate. The candidate's price in the
// response is what the item would cost when added to the cart now.

const CANDIDATE_ITEM_REF = 'strike-price-item';

export type StrikePriceCandidate = {
  // identifies the candidate's basket in the response
  ref: string;
  // unit price before coupon discounts, including the volume discount
  minorUnitPrice: number;
  productTypeId: number;
  volumeDiscount: boolean;
};

export type StrikePriceContext = {
  cartAndLineItemGroups: CartAndLineItemGroups | null | undefined;
  currency: string;
  timezone: string;
  coupons: string[];
  // keyed by shop id; must contain the cart's shops and `candidateShopId`
  categoryIdsByShop: Record<string, CategoryIdsByProductType>;
  candidateShopId: string;
};

export type StrikePrice = { minorUnitOriginalPrice: number; minorUnitItemPrice: number };

const StrikePriceResponseSchema = z.object({
  baskets: z.array(
    z.object({
      ref: z.nullish(z.string()),
      items: z.array(
        z.object({
          ref: z.nullish(z.string()),
          minorUnitOriginalPrice: z.number(),
          minorUnitItemPrice: z.number(),
        })
      ),
    })
  ),
});

const toCandidateItem = (
  candidate: StrikePriceCandidate,
  categoryIds: CategoryIdsByProductType | undefined
): ValidationItem => ({
  ref: CANDIDATE_ITEM_REF,
  minorUnitPrice: candidate.minorUnitPrice,
  quantity: 1,
  productTypeId: candidate.productTypeId,
  productTypeCategoryIds: categoryIds?.get(String(candidate.productTypeId)) ?? [],
  volumeDiscount: candidate.volumeDiscount,
  businessUnit: BUSINESS_UNIT,
});

export const buildStrikePriceRequest = (
  context: StrikePriceContext,
  candidates: StrikePriceCandidate[]
): CouponValidationRequest => {
  const cartItems = context.cartAndLineItemGroups
    ? getCartValidationItems(context.cartAndLineItemGroups, context.categoryIdsByShop)
    : [];
  const candidateCategoryIds = context.categoryIdsByShop[context.candidateShopId];

  return {
    baskets: candidates.map((candidate) => ({
      ref: candidate.ref,
      currency: context.currency,
      timezone: context.timezone,
      coupons: context.coupons.map((code) => ({ code })),
      items: [...cartItems, toCandidateItem(candidate, candidateCategoryIds)],
    })),
  };
};

// Candidate prices by basket ref.
export const parseStrikePriceResponse = (response: unknown): Map<string, StrikePrice> => {
  const prices = new Map<string, StrikePrice>();

  StrikePriceResponseSchema.parse(response).baskets.forEach((basket) => {
    const item = basket.items.find((i) => i.ref === CANDIDATE_ITEM_REF);
    if (basket.ref && item) {
      prices.set(basket.ref, {
        minorUnitOriginalPrice: item.minorUnitOriginalPrice,
        minorUnitItemPrice: item.minorUnitItemPrice,
      });
    }
  });

  return prices;
};
